import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { trpc } from "./utils/trpc.ts";
import { BrowserRouter } from "react-router";
import { AuthProvider } from "./providers/Auth/AuthProvider.tsx";
import { ToastProvider } from "./providers/Toast/ToastProvider.tsx";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { obsidianTheme } from "./constants/obsidianTheme.ts";
import { PostHogProvider } from "@repo/posthog/react";
import { StatusCode } from "@repo/utils/http";
import { ObsidianEvent } from "./events.ts";
import { SquareBackground } from "@repo/dashboard-components";

const queryClient = new QueryClient();
const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      fetch: async (input, init) => {
        const response = await fetch(input, init);

        if (response.status === StatusCode.Unauthorized) {
          window.dispatchEvent(
            new ObsidianEvent("ow:api-unauthenticated-response", {
              detail: null,
            }),
          );
        }

        return response;
      },
    }),
  ],
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: "2025-05-24",
        capture_exceptions: true,
        debug: import.meta.env.MODE === "development",
      }}
      fallback={<SquareBackground mode="corner" />}
    >
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename="/dashboard">
            <AuthProvider>
              <ThemeProvider theme={obsidianTheme}>
                <CssBaseline />
                <ToastProvider>
                  <App />
                </ToastProvider>
              </ThemeProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </trpc.Provider>
    </PostHogProvider>
  </React.StrictMode>,
);
